/* eslint-disable import/no-cycle */
import uuid from 'uuid/v4';
import {
  toLower,
  cloneDeep,
  forEach,
  startsWith,
  upperCase,
  isEmpty,
} from 'lodash';
import AutocodeLabelsGenerator, {
  ACStandardLabels,
} from '../components/autocode/AutocodeLabelsGenerator';
import AutocodeExpressionListGenerator from '../components/autocode/AutocodeExpressionListGenerator';
import {
  combineAllChildren,
  getExpressionFormed,
  checkKeywords,
  buildDictionaryItem,
  addOperatorItem,
  getObjectAdded,
  functionsInComposer,
  getKeywordList,
  createDictionaryElementForKeyword
} from '../components/autocode/AutocodeFunctions';
import {
  getDicForDefKeywords,
  fetchNodeObjectForDefinition,
} from '../actions/autocodeActions';
import { GEOGRAPHY_BASES } from '../actions/actionTypes';
import {
  AC_NODETYPE_ROOT,
  AC_NODETYPE_DATAPOINT,
  AC_NODETYPE_DEFINITION,
  AC_NODETYPE_OPERATOR,
  AC_NODETYPE_FUNC,
  AC_COUNT_OPTIONS,
  AC_OPERATORS,
  TEMPLATE_TYPE_1,
  TEMPLATE_TYPE_2,
  TEMPLATE_TYPE_3,
  TEMPLATE_TYPE_4,
  TEMPLATE_TYPE_5,
  TEMPLATE_TYPE_7,
  TEMPLATE_TYPE_8,
  TEMPLATE_TYPE_9,
  AC_FUNTIONS_BASED_ON_TEMPLATE,
  TEMPLATE_1_NODE_CATEGORY,
  MAX_EXPORT_LABEL_LIMIT,
} from '../components/autocode/AutocodeConstants';
import { wrapNode } from '../components/autocode/ACContext/acSelectionBoxReducer';
import { dictionaryDetailExp } from './CrosstabUtils';

const OperatorArray = ['AND', 'OR', 'XOR', 'AND NOT', 'OR NOT'];

let dictionaryItemsResponse = {};

export const _getStudyGroupedData = data => {
  const obj = {
    personal: [],
    shared: [],
    global: [],
  };

  if (data.length > 0) {
    data.map(item => {
      if (item.scope === 4 || item.scope === 5) {
        obj.personal = [...obj.personal, item];
      }

      if (item.scope === 2 || item.scope === 3 || item.scope === 6) {
        obj.shared = [...obj.shared, item];
      }

      if (item.scope === 0 || item.scope === 1) {
        obj.global = [...obj.global, item];
      }

      return false;
    });
  }
  return obj;
};

export const filterLabel = (title, breadcrumbs) => {
  const breadcrumb = breadcrumbs.split(' > ');

  if (breadcrumb[0] && breadcrumb[0].toUpperCase() === 'DEFINITIONS') {
    return `${title}`;
  }

  return `${title}: ${breadcrumbs}`;
};

export const updateCrosstabTitleHeader = (updatedData, metaData) => {
  const originalList = metaData.originalList;
  let index = 0;
  if (originalList.length !== 0) {
    index = originalList.findIndex(item => {
      if (item.keyword === updatedData.keyword) {
        return true;
      }
      return false;
    });
    updatedData.index = index;
  }
  updatedData.keyword = originalList[index].keyword;
  const dataSmartSearch = originalList[index].dataSmartSearch;
  const oldLabel = filterLabel(
    `${dataSmartSearch.objectsAdded[0].shortTextCatgView}`,
    `${dataSmartSearch.objectsAdded[0].breadcrumbs}`
  );
  const isLabelUpdated = !(oldLabel === updatedData.updatedTitle);
  updatedData = {
    ...updatedData,
    isLabelUpdated,
  };
  metaData.updateCrosstabGridTitleHeader(updatedData);
};

/*
compute the height of middle section if it has two children
height = total height of middlesection
childA = first children
childB = second children
it return = [heightOfFirstChildren, heightOfSecondChildren] */

/**
 * Moved the code from SmartsearchEditor to util. The below functions will build autocodenodeobject
 * Start of buildAutoCodeObject and its dependencies from smartsearch or dictionary search
 */

const createAutoCodeDataType = (type, labels, data, splitData) => {
  let codeData = {};
  if (type === 'operator') {
    codeData.selected = false;
    codeData.type = { label: labels, symbol: labels };
  } else {
    codeData = {
      labels: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].labels
        : labels,
      nodeLabel: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].labels[0]
        : splitData[0],
      datatype: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].recType
        : data.rectype,
      keyword: data.keyword,
      path: data.pathId,
      node: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].node
        : data.node,
      midpoint: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].midpoint
        : data.midpoint,
      modeled: data.modeled,
      objectType: data.objectType,
      recordType: data.recordType,
      rectype: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].recType
        : data.rectype,
      shortText: data.shortText,
      shortTextCatgView: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].labels[1]
        : splitData[1],
      textPath: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].textPath
        : data.textPath,
      uniqueId: data.uniqueId,
      breadcrumbs: data.breadcrumbs,
      template: dictionaryItemsResponse[data.keyword]
        ? dictionaryItemsResponse[data.keyword].templateType
        : data.template,
    };
    if (type === 'definition') {
      codeData.title = data.title;
      codeData.expression = data.keyword;
    }
  }
  return codeData;
};

export const truncateDefLabel = (label, limit) => {
  // return label if under the limit
  if (label.length < limit) return label;

  // truncate label, check encoded length, and return if under the limit
  const truncated = `${label.substring(0, limit - 3)}...`;
  const encoded = new TextEncoder().encode(truncated);
  if (encoded.length < limit) return truncated;

  // otherwise truncate more characters and return
  return `${label.substring(0, limit - 6)}...`;
};

const createAutoCodeObject = (
  children,
  data,
  type,
  rootnodeChild,
  isLabelUpdated,
  labelOverride
) => {
  const { toLabelString, toDefString } = AutocodeLabelsGenerator(
    ACStandardLabels,
    null
  );
  const { buildObjectsAddedArray } = AutocodeExpressionListGenerator();

  let objType = type;
  if (objType !== 'operator') {
    if ((data.recType && data.recType === 'D') || data.ovDefinitionId) {
      objType = 'definition';
    } else {
      objType = 'dictionary';
    }
  }

  const labels = [];
  const splitData = [];
  if (data.shortText) {
    splitData[0] = data.shortText.split(/:(.+)/)[0];
    if (data.shortText.split(/:(.+)/)[1]) {
      splitData[1] = data.shortText.split(/:(.+)/)[1];
    }
  }
  const breadCrumbs = data.breadcrumbs ? data.breadcrumbs.split(' > ') : [];
  if (data.breadcrumbs && !('labels' in data)) {
    for (let i = 0; i < breadCrumbs.length - 1; i += 1) {
      labels[i] = breadCrumbs[i];
    }
    if (splitData.length === 2 && splitData[1] !== '') {
      labels[breadCrumbs.length - 1] = toLower(splitData[0]);
      labels[breadCrumbs.length] = toLower(splitData[1]);
    } else {
      labels[breadCrumbs.length - 1] = toLower(
        breadCrumbs[breadCrumbs.length - 1]
      );
      labels[breadCrumbs.length] = toLower(splitData[0]);
    }
  }
  /* { active: true,
     breadcrumbs: "Geographies > ZIP > ",
     fname: "INZIP",
     name: "01031 - GILBERTVILLE",
     nodeId: 1031,
     objectType: "operand",
     parent: 600506,
     shortTextCatgView: "01031 - GILBERTVILLE",
     type: "L" }
    if data.fname is present its a geography item and for the answers label we add
    shortTextCatgView instead of shortText to the labels */
  if (data.fname) {
    labels.pop();
    labels.push(toLower(data.shortTextCatgView));
    if (GEOGRAPHY_BASES.includes(data.fname)) {
      data.keyword = `${data.fname} ( ${data.nodeId} )`;
      data.rectype = data.type;
      data.recordType = 'G';
      data.uniqueId = uuid();
    }
  }

  const autocodenode = {
    type: objType,
    id: uuid(),
    selected: false,
    // combine: false,
    combine() {
      return combineAllChildren(this);
    },
    data:
      type === 'operator'
        ? createAutoCodeDataType('operator', data.keyword, data, splitData)
        : createAutoCodeDataType(
            objType,
            'labels' in data ? data.labels : labels,
            data,
            splitData
          ),
    children,
    group: 0,
    rootnodeChild,
    toLabelString(pts) {
      return toLabelString(this, pts);
    },
    toExpressionString() {
      return toDefString(this);
    },
    toExpressionArray(pts) {
      return buildObjectsAddedArray(this, pts);
    },
  };

  if (rootnodeChild === 'root') {
    autocodenode.useLabelOverride = !!(
      labelOverride && labelOverride.trim().length > 0
    );
    autocodenode.labelOverride =
      labelOverride && labelOverride.trim().length > 0 ? labelOverride : '';
  }

  return autocodenode;
};

const findFirstOperatorInExp = (exp, objectsAdded) => {
  let parentIndex = null;
  objectsAdded.forEach((item, index) => {
    if (
      item.objectType === 'operator' &&
      item.keyword !== 'NOT' &&
      parentIndex === null
    ) {
      parentIndex = index;
    }
  });
  return parentIndex;
};

const createRightSideExpressionNode = (
  rightExp,
  objectsAdded,
  parentChildTreeArr,
  rootnodeChild,
  isLabelUpdated,
  labelOverride
) => {
  const rightnodeArr = [];
  const rightNodeChildrenArr = [];
  const firstOperatorIndex =
    rightExp.length > 2 ? findFirstOperatorInExp(rightExp, objectsAdded) : null;
  if (firstOperatorIndex === null) {
    objectsAdded.forEach(obj => {
      if (rightExp.indexOf(obj.keyword) !== -1) {
        rightnodeArr.push(
          createAutoCodeObject(
            [],
            obj,
            obj.objectType,
            rootnodeChild,
            isLabelUpdated,
            labelOverride
          )
        );
      }
    });
  } else {
    const newRightExp = [];
    let parentObj = {};
    const leftItems = [];
    rightExp.forEach((elems, index) => {
      const obj = objectsAdded.filter(obj => elems === obj.keyword);
      if (index < firstOperatorIndex) {
        rightNodeChildrenArr.push(
          createAutoCodeObject(
            [],
            obj[0],
            obj[0].objectType,
            rootnodeChild,
            isLabelUpdated,
            labelOverride
          )
        );
        leftItems.push(elems);
      } else if (index > firstOperatorIndex) {
        newRightExp.push(elems);
      } else {
        parentObj = obj[0];
      }
    });
    if (newRightExp.length > 2) {
      parentChildTreeArr =
        parentChildTreeArr === undefined ? [] : parentChildTreeArr;
      if (parentChildTreeArr.length === 0) {
        parentChildTreeArr.push([rightExp[firstOperatorIndex]]);
        parentChildTreeArr.push(leftItems);
      } else {
        const lastNode =
          parentChildTreeArr.length === 0
            ? []
            : parentChildTreeArr[parentChildTreeArr.length - 1];
        lastNode.push(rightExp[firstOperatorIndex]);
        parentChildTreeArr.pop();
        parentChildTreeArr.push(lastNode);
        parentChildTreeArr.push(leftItems);
      }
      // recursive call, to break rest expression node.
      return createRightSideExpressionNode(
        newRightExp,
        objectsAdded,
        parentChildTreeArr,
        'operator',
        isLabelUpdated,
        labelOverride
      );
    }
    if (parentChildTreeArr && parentChildTreeArr.length > 0) {
      const lastNode = parentChildTreeArr[parentChildTreeArr.length - 1];
      lastNode.push(rightExp[firstOperatorIndex]);
      parentChildTreeArr.pop();
      parentChildTreeArr.push(lastNode);
      parentChildTreeArr.push([...leftItems, ...newRightExp]);
      parentChildTreeArr.reverse();
      const nodesArr = [];
      let nodesChildrenArr = [];
      parentChildTreeArr.forEach((eachNodes, eachIndex) => {
        const childrenNodeArr = [];
        eachNodes.forEach((nodes, index) => {
          const obj = objectsAdded.filter(obj => nodes === obj.keyword);
          if (eachIndex === 0) {
            // first index which are last children
            nodesChildrenArr.push(
              createAutoCodeObject(
                [],
                obj[0],
                obj[0].objectType,
                rootnodeChild,
                isLabelUpdated,
                labelOverride
              )
            );
          } else if (eachIndex === parentChildTreeArr.length - 1) {
            // last main node
            rightnodeArr.push(
              createAutoCodeObject(
                nodesChildrenArr,
                obj[0],
                obj[0].objectType,
                'root',
                isLabelUpdated,
                labelOverride
              )
            );
          } else {
            // middle indexes
            if (index === eachNodes.length - 1) {
              childrenNodeArr.push(
                createAutoCodeObject(
                  nodesChildrenArr,
                  obj[0],
                  obj[0].objectType,
                  rootnodeChild,
                  isLabelUpdated,
                  labelOverride
                )
              );
              nodesChildrenArr = [];
              nodesChildrenArr = [...childrenNodeArr];
            } else {
              childrenNodeArr.push(
                createAutoCodeObject(
                  [],
                  obj[0],
                  obj[0].objectType,
                  rootnodeChild,
                  isLabelUpdated,
                  labelOverride
                )
              );
            }
          }
        });
      });
    } else {
      newRightExp.forEach((elems, index) => {
        const obj = objectsAdded.filter(obj => elems === obj.keyword);
        rightNodeChildrenArr.push(
          createAutoCodeObject(
            [],
            obj[0],
            obj[0].objectType,
            rootnodeChild,
            isLabelUpdated,
            labelOverride
          )
        );
      });
      rightnodeArr.push(
        createAutoCodeObject(
          rightNodeChildrenArr,
          parentObj,
          parentObj.objectType,
          'root',
          isLabelUpdated,
          labelOverride
        )
      );
    }
  }
  return rightnodeArr;
};

/** check if the expression is within () */
const checkIfExpWithinParanthesis = (expressionFormed, objectsAdded) => {
  let paransBracesCount = 0;
  let midParans = false;

  let expWithinParanthesis = false;
  const newExpression = [...expressionFormed];
  const newObjAdded = [...objectsAdded];
  const lastIndex = expressionFormed.length - 1;

  if (newExpression[0] === '(' && newExpression[lastIndex] === ')') {
    newExpression.forEach((item, index) => {
      if (item === '(') {
        paransBracesCount += 1;
      } else if (item === ')' && index !== lastIndex) {
        paransBracesCount -= 1;
        midParans = midParans === false ? paransBracesCount === 0 : midParans;
      } else if (
        item === ')' &&
        index === lastIndex &&
        paransBracesCount === 1 &&
        !midParans
      ) {
        paransBracesCount -= 1;
        expWithinParanthesis = true;
      }
    });
  }
  if (expWithinParanthesis) {
    newExpression.shift();
    newExpression.pop();
    newObjAdded.shift();
    newObjAdded.pop();
    return checkIfExpWithinParanthesis(newExpression, newObjAdded);
  }
  return { expressionFormed: newExpression, objectsAdded: newObjAdded };
};

/** returns the parent operator Index in any expressions with () */
const checkParentOperatorIndexInExp = (expressionFormed, objectsAdded) => {
  let paransBracesCount = 0;
  let parentOperatorIndex = null;
  expressionFormed.forEach((item, index) => {
    if (parentOperatorIndex === null) {
      if (item === '(') {
        paransBracesCount += 1;
      } else if (item === ')') {
        paransBracesCount -= 1;
      } else if (
        paransBracesCount === 0 &&
        OperatorArray.indexOf(item) !== -1
      ) {
        parentOperatorIndex = index;
      }
    }
  });
  return parentOperatorIndex;
};

const createAutoCodeObjForExpWithParan = (
  expressionFormed,
  objectsAdded,
  rootnodeChild,
  isLabelUpdated,
  labelOverride
) => {
  const newExpression = checkIfExpWithinParanthesis(
    cloneDeep(expressionFormed),
    cloneDeep(objectsAdded)
  );
  const newExpressionFormed = newExpression.expressionFormed;
  const newObjectsAdded = newExpression.objectsAdded;

  let parentNode = {};
  let leftNode = {};
  let rightNode = {};

  if (newExpressionFormed.indexOf('(') !== -1) {
    const parentOperatorIndex = checkParentOperatorIndexInExp(
      newExpressionFormed,
      newObjectsAdded
    );
    const leftSideItems = [];
    const leftSideObjects = [];
    const rightSideItems = [];
    const rightSideObjects = [];
    // first push the parent
    newExpressionFormed.forEach((item, index) => {
      if (index === parentOperatorIndex) {
        // parent element
        parentNode.label = item;
        const parentObj = newObjectsAdded.filter(
          (obj, objIndex) => objIndex === index
        );
        parentNode.objectsAdded = parentObj[0];
        parentNode.type = 'operator';
      } else if (index < parentOperatorIndex) {
        // left side of the opearator
        leftSideItems.push(item);
        const leftObj = newObjectsAdded.filter(
          (obj, objIndex) => objIndex === index
        );
        leftSideObjects.push(leftObj[0]);
      } else if (index > parentOperatorIndex) {
        // right side of operator
        rightSideItems.push(item);
        const rightObj = newObjectsAdded.filter(
          (obj, objIndex) => objIndex === index
        );
        rightSideObjects.push(rightObj[0]);
      }
    });

    if (leftSideItems.length > 2) {
      leftNode = createAutoCodeObjForExpWithParan(
        leftSideItems,
        leftSideObjects,
        'operator'
      );
    } else {
      leftNode = createAutoCodeObject(
        [],
        leftSideObjects[0],
        'dictionary',
        rootnodeChild,
        isLabelUpdated,
        labelOverride
      );
    }

    if (rightSideItems.length > 2) {
      rightNode = createAutoCodeObjForExpWithParan(
        rightSideItems,
        rightSideObjects,
        'operator'
      );
    } else {
      rightNode = createAutoCodeObject(
        [],
        rightSideObjects[0],
        'dictionary',
        rootnodeChild,
        isLabelUpdated,
        labelOverride
      );
    }

    parentNode.children = [leftNode, rightNode];
    return createAutoCodeObject(
      parentNode.children,
      parentNode.objectsAdded,
      parentNode.type,
      rootnodeChild,
      isLabelUpdated,
      labelOverride
    );
  }
  // no paranthesis left in the expression
  parentNode = createRightSideExpressionNode(
    newExpressionFormed,
    newObjectsAdded,
    [],
    rootnodeChild,
    isLabelUpdated,
    labelOverride
  );
  if (Array.isArray(parentNode)) {
    parentNode = parentNode[0];
  }
  return parentNode;
};

export const buildAutoCodeObject = (
  expressionFormed,
  objectsAdded,
  dictionaryDetails,
  isLabelUpdated = false,
  labelOverride = ''
) => {
  let autocodenode = {};
  if (dictionaryDetails) {
    dictionaryItemsResponse = dictionaryDetails;
  }
  try {
    if (objectsAdded.length < 3 && objectsAdded.length !== 0) {
      // only 1 operand along with NOT operator
      autocodenode = createAutoCodeObject(
        [],
        objectsAdded[0],
        'dictionary',
        'root',
        isLabelUpdated,
        labelOverride
      );
    } else if (objectsAdded.length > 1) {
      if (expressionFormed.includes('(')) {
        // The place to create Tree Structure here
        autocodenode = createAutoCodeObjForExpWithParan(
          expressionFormed,
          objectsAdded,
          'root',
          isLabelUpdated,
          labelOverride
        );
      } else {
        // Only operators and operands. Ex: A AND B OR C
        autocodenode = createRightSideExpressionNode(
          expressionFormed,
          objectsAdded,
          [],
          'root',
          isLabelUpdated,
          labelOverride
        );
        if (Array.isArray(autocodenode)) {
          autocodenode = autocodenode[0];
        }
      }
    }
  } catch (e) {
    console.warn('exception while creating autocodenode obj: ', e);
  }
  return autocodenode;
};

export const extractGeoExpression = (
  expression,
  newExpressionsList,
  selectedKeyword
) => {
  const newOperators = [
    'AND',
    'OR',
    'XOR',
    'AND NOT',
    'OR NOT',
    'NOT',
    '(',
    ')',
  ];
  const indexOfKeyword = expression.indexOf(selectedKeyword);
  // const strAfterKeyword = expression.substring(indexOfKeyword, expression.length);
  const indexOfClosingParen = expression.indexOf(')') + 1;
  const geoData = expression.substring(indexOfKeyword, indexOfClosingParen);
  if (geoData.includes(',')) {
    const getMultipleGeoPts = geoData
      .substring(geoData.indexOf('(') + 1, geoData.indexOf(')'))
      .trim();
    const splittedData = getMultipleGeoPts.split(',');
    splittedData.forEach(item => {
      newExpressionsList.push(`${selectedKeyword} ( ${item.trim()} )`);
    });
  } else {
    newExpressionsList.push(geoData);
  }
  const result = expression.split('');
  result.splice(indexOfKeyword, indexOfClosingParen - indexOfKeyword);
  const strWithoutGeoData = result.join('').trim();
  let isGeo = false;
  let tempKeyword = '';
  GEOGRAPHY_BASES.forEach(keyword => {
    if (strWithoutGeoData.includes(keyword) && tempKeyword === '') {
      isGeo = true;
      tempKeyword = keyword;
    }
  });
  if (isGeo) {
    newExpressionsList = extractGeoExpression(
      strWithoutGeoData,
      newExpressionsList,
      tempKeyword
    );
  } else if (strWithoutGeoData.trim()) {
    const splitExp = strWithoutGeoData.split(' ');
    splitExp.forEach(exp => {
      if (newOperators.indexOf(exp) === -1) {
        if (exp[0] === '(') {
          exp = exp.substring(1);
        }
        if (exp[exp.length - 1] === ')') {
          exp = exp.substring(0, exp.length - 1);
        }
        newExpressionsList.push(exp);
      }
    });
  }
  return newExpressionsList;
};

export const divideJointExpressions = expression => {
  let newExpressionsList = [];
  expression.forEach(expressions => {
    let isgeoDataPoint = false;
    let selectedKeyword = '';
    GEOGRAPHY_BASES.forEach(keyword => {
      if (expressions.includes(keyword) && selectedKeyword === '') {
        isgeoDataPoint = true;
        selectedKeyword = keyword;
      }
    });
    if (isgeoDataPoint) {
      newExpressionsList = extractGeoExpression(
        expressions.trim(),
        newExpressionsList,
        selectedKeyword
      ).filter(item => checkKeywords(item));
    } else {
      const splitExp = getExpressionFormed(expressions);
      newExpressionsList.push(...splitExp.filter(item => checkKeywords(item)));
    }
  });
  newExpressionsList = [...new Set(newExpressionsList)];
  return newExpressionsList;
};

/**
 * end of buildautocode from smartsearch or dictionary search
 */

/**
 * This util function helps to get all the data points of defintions or dictionary
 * at any child level. thi sarray is assigned to ObjectsAdded for functions to support
 * backward compatability
 */
export const getAllDefintionsorDictionaryNodes = node => {
  const objectsAdded = [];
  const getChildnodes = _node => {
    forEach(_node.children, child => {
      if (child.type === 'defintion' || child.type === 'dictionary') {
        objectsAdded.push(child.data);
      }
      if (child.children.length > 0) {
        return getChildnodes(child);
      }
    });
  };
  getChildnodes(node);
  return objectsAdded;
};

/**
 * will return final label from autocodenode object based on Autocode label dropdown configuration
 * which sent as part of partsToShow
 */
export const getLabel = (rowData, partsToShow) => {
  const { toLabelString } = AutocodeLabelsGenerator(ACStandardLabels, null);
  if (rowData && !rowData.dataSmartSearch) {
    return '';
  }

  if (rowData && rowData.isLabelUpdated) {
    return rowData.originalTitle;
  }
  if (
    rowData &&
    rowData.dataSmartSearch &&
    rowData.dataSmartSearch.autocodenode &&
    !(
      Object.keys(rowData.dataSmartSearch.autocodenode).length === 0 &&
      rowData.dataSmartSearch.autocodenode.constructor === Object
    )
  ) {
    const title = toLabelString(
      rowData.dataSmartSearch.autocodenode,
      partsToShow
    );
    if (title.includes('error')) return rowData.originalTitle;
    return title;
  }
  if (rowData && rowData.originalTitle) {
    return rowData.originalTitle;
  }
  return '';
};

// ***********************************************************************************************
// Code to rebuild the Autocodenode for functions

const checkTemplateTypeandSendFunction = (functionType, children) => {
  let funcName;
  if (
    children.every(
      (item, index, arr) => item.data.template === arr[0].data.template
    )
  ) {
    const templateType = children[0].data.template;
    const nodeLabel =
      templateType === TEMPLATE_TYPE_1 &&
      children.every(
        (item, index, arr) =>
          item.data.nodeLabel === arr[0].data.nodeLabel &&
          item.data.nodeLabel === TEMPLATE_1_NODE_CATEGORY
      )
        ? TEMPLATE_1_NODE_CATEGORY
        : '';

    switch (templateType) {
      case TEMPLATE_TYPE_1: {
        switch (nodeLabel) {
          case TEMPLATE_1_NODE_CATEGORY: {
            // AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_1_CATEGORY
            if (functionType === 'MEAN') {
              funcName =
                AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_1_CATEGORY[1].funcName;
            } else if (functionType === 'MEDIAN') {
              funcName =
                AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_1_CATEGORY[0].funcName;
            } else if (functionType === 'VOLUME') {
              funcName =
                AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_1_CATEGORY[2].funcName;
            }
            break;
          }
          default: {
            // AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_1_All
            if (functionType === 'MEAN') {
              funcName =
                AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_1_All[1].funcName;
            } else if (functionType === 'VOLUME') {
              funcName =
                AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_1_All[0].funcName;
            }
            break;
          }
        }
        break;
      }
      case TEMPLATE_TYPE_2:
      case TEMPLATE_TYPE_8: {
        // AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_2_8;
        if (functionType === 'MEAN') {
          funcName = AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_2_8[0].funcName;
        } else if (functionType === 'MEDIAN') {
          funcName = AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_2_8[1].funcName;
        }
        break;
      }
      case TEMPLATE_TYPE_7: {
        if (functionType === 'MEAN') {
          funcName = AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_7[0].funcName;
        }
        break;
      }
      case TEMPLATE_TYPE_4: {
        // AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_4;
        if (functionType === 'MEAN') {
          funcName = AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_4[0].funcName;
        }
        break;
      }
      case TEMPLATE_TYPE_3:
      case TEMPLATE_TYPE_5:
      case TEMPLATE_TYPE_9: {
        // AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_3_5_9;
        if (functionType === 'MEAN') {
          funcName = AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_3_5_9[0].funcName;
        } else if (functionType === 'MEDIAN') {
          funcName = AC_FUNTIONS_BASED_ON_TEMPLATE.TEMPLATE_3_5_9[1].funcName;
        }
        break;
      }
      default:
        break;
    }
  }
  return funcName;
};

export const buildNode = (
  node,
  children,
  datatype,
  isLabelUpdated = false,
  originalTitle = ''
) => {
  const { toLabelString, toDefString } = AutocodeLabelsGenerator(
    ACStandardLabels,
    null
  );
  const { buildObjectsAddedArray } = AutocodeExpressionListGenerator();

  const type = datatype;
  let data = {};

  if (datatype === AC_NODETYPE_DATAPOINT) {
    // For dictionary childrens
    data = node;
  } else if (datatype === AC_NODETYPE_FUNC) {
    // Type of Functions goes here

    switch (node.functionName) {
      case 'COUNTRANGE': {
        data.funcName = AC_COUNT_OPTIONS[0].funcName;
        data.selected = false;
        data.editable = true;
        data.params = {
          lower: node.lowerBound,
          upper: node.upperBound,
          operator: {},
        };
        break;
      }
      case 'MEAN':
      case 'MEANSCORE':
      case 'MEANNOZERO':
      case 'MEANSCORENOZERO':
      case 'MEANNONULL':
      case 'MEANSCORENONULL': {
        data.funcName = checkTemplateTypeandSendFunction(
          node.functionName === 'MEAN'
            ? node.functionName
            : node.functionName.substring(0, 4),
          children
        );
        if (data.funcName) {
          data.selected = false;
          data.editable = true;
          data.params = {
            lower: node.lowerBound,
            upper: node.upperBound,
            operator: {},
          };
        }
        break;
      }
      case 'VOLUME': {
        data.funcName = checkTemplateTypeandSendFunction(
          node.functionName,
          children
        );
        if (data.funcName) {
          data.selected = false;
          data.editable = true;
          data.params = {
            lower: node.lowerBound,
            upper: node.upperBound,
            operator: {},
          };
        } else {
          data.funcName = '';
          data.selected = false;
          data.editable = false;
          data.params = {
            operator: {},
          };
        }
        break;
      }
      case 'MEDIAN':
      case 'MEDIANDISCRETE':
      case 'MEDIANDISCRETENOZERO':
      case 'MEDIANNOZERO': {
        data.funcName = checkTemplateTypeandSendFunction(
          node.functionName === 'MEDIAN'
            ? node.functionName
            : node.functionName.substring(0, 6),
          children
        );
        if (data.funcName) {
          data.selected = false;
          data.editable = true;
          data.params = {
            lower: node.lowerBound,
            upper: node.upperBound,
            operator: {},
          };
        }
        break;
      }
      case 'NETANY': {
        data.funcName = 'net any';
        data.selected = false;
        data.editable = true;
        data.params = {
          operator: {
            label: node.label,
            symbol: node.symbol,
          },
        };
        break;
      }
      case 'SUM': {
        data.funcName = node.functionName;
        data.selected = false;
        data.editable = true;
        data.params = {
          operator: {},
        };
        break;
      }

      default: {
        data.funcName = '';
        data.selected = false;
        data.editable = false;
        data.params = {
          operator: {},
        };
        break;
      }
    }
  }
  return {
    type,
    id: uuid(),
    selected: false,
    data,
    children,
    group: 0,
    rootnodeChild:
      datatype === AC_NODETYPE_FUNC ? AC_NODETYPE_ROOT : AC_NODETYPE_FUNC,
    useLabelOverride: isLabelUpdated[0],
    labelOverride: originalTitle,
    combine() {
      return combineAllChildren(this);
    },
    toLabelString(pts) {
      return toLabelString(this, pts);
    },
    toExpressionString() {
      return toDefString(this);
    },
    toExpressionArray(pts) {
      return buildObjectsAddedArray(this, pts);
    },
  };
};

export const removeStartEndParanthesis = expressionList => {
  const copyExpList = [...expressionList];

  if (copyExpList[0] === '(' && copyExpList[copyExpList.length - 1] === ')') {
    copyExpList.shift();
    copyExpList.pop();
  } else return copyExpList;

  return removeStartEndParanthesis(copyExpList);
};

export const buildFunctionNode = (
  keywordListData,
  keyword,
  expressionFormed,
  objectsAdded,
  isLabelUpdated,
  originalTitle,
  templateInfo = false
) => {
  const children = [];

  let functionName = functionsInComposer.find(item =>
    getExpressionFormed(keyword)
      .map(elem => elem.toUpperCase())
      .includes(item)
  );
  // let functionName = removeStartEndParanthesis(
  //   getExpressionFormed(keyword)
  // )[0].toUpperCase();

  expressionFormed.forEach(item => {
    // const node = cloneDeep(item);
    const node = cloneDeep(
      objectsAdded.filter(elem => elem.keyword === item)
    )[0];
    const keyData = keywordListData[item];

    if (node.objectType === 'operand' && !templateInfo) {
      node.labels = keyData.labels;
      node.template = keyData.templateType;
      node.nodeLabel =
        keyData.templateType === TEMPLATE_TYPE_1 &&
        keyData.labels.includes(TEMPLATE_1_NODE_CATEGORY)
          ? TEMPLATE_1_NODE_CATEGORY
          : keyData.templateType === TEMPLATE_TYPE_1 &&
            keyData.labels.includes('Brand')
          ? 'Brand'
          : keyData.templateType === TEMPLATE_TYPE_1 &&
            keyData.labels.includes('Type')
          ? 'Type'
          : keyData.labels[keyData.labels.length - 2];
      node.path = node.pathId;
      node.datatype = keyData.recType;
      node.breadcrumbs = keyData.labels.join(' > ');
    }
    children.push(buildNode(node, [], AC_NODETYPE_DATAPOINT, [false], ''));
  });

  // need to check this as Net any not possible for nested elements as of now
  if (expressionFormed.every(item => startsWith(upperCase(item), 'XNA'))) {
    functionName = 'NETANY';
  } else if (keyword.includes(' + ')) {
    functionName = 'SUM';
  }

  const node = {
    functionName,
    upperBound:
      functionName === 'COUNTRANGE'
        ? parseInt(
            getExpressionFormed(keyword).filter(item => /^\d+$/.test(item))[1],
            10
          )
        : 1,
    lowerBound:
      functionName === 'COUNTRANGE'
        ? parseInt(
            getExpressionFormed(keyword).filter(item => /^\d+$/.test(item))[0],
            10
          )
        : 1,
    label: keyword.includes(' OR ') ? 'OR' : 'AND',
    symbol: keyword.includes(' OR ') ? 'OR' : 'AND',
  };

  return buildNode(
    node,
    children,
    AC_NODETYPE_FUNC,
    isLabelUpdated,
    originalTitle
  );
};

// takes an array
// [ "(","MALE","OR","INSTATE","(","45","23","19",")","AND","FEMALE","OR","INCOUNTY","(","22037",")",")" ]
// and returns
// [ "(","MALE","OR","INSTATE ( 45 , 23 , 19 )","AND","FEMALE","OR","INCOUNTY ( 22037 )",")" ]

export const mergeGeographyNodes = expressionList => {
  let strtIndex = 0;
  let endIndex = 0;
  const updatedExpressionlist = [];

  while (strtIndex < expressionList.length) {
    if (GEOGRAPHY_BASES.includes(expressionList[strtIndex])) {
      let geoNodeExpression = '';
      endIndex = strtIndex;

      while (expressionList[endIndex] !== ')') {
        if (
          /^\d*$/.test(expressionList[endIndex]) &&
          /^\d*$/.test(expressionList[endIndex - 1])
        ) {
          geoNodeExpression = `${geoNodeExpression} , ${expressionList[endIndex]}`;
        } else {
          geoNodeExpression = `${geoNodeExpression} ${expressionList[endIndex]}`;
        }
        endIndex += 1;
      }
      geoNodeExpression = `${geoNodeExpression} ${expressionList[endIndex]}`;

      updatedExpressionlist.push(geoNodeExpression);
      strtIndex = endIndex + 1;
    } else {
      updatedExpressionlist.push(expressionList[strtIndex]);
      strtIndex += 1;
    }
  }
  return updatedExpressionlist;
};

export const checkAndRemoveBalancedParanthesis = expressionList => {
  const expressionArr = removeStartEndParanthesis(expressionList);
  let notUsedInExpression = false;
  const strtIndex = 1;
  let endIndex = strtIndex;
  const paransBracesCount = [];
  paransBracesCount.push(expressionArr[strtIndex]);
  let endOfString = false;

  while (paransBracesCount.length > 0) {
    endIndex += 1;

    if (expressionArr[endIndex] === '(')
      paransBracesCount.push(expressionArr[endIndex]);
    else if (expressionArr[endIndex] === ')') paransBracesCount.pop();
    else if (expressionArr[endIndex] === AC_OPERATORS.type.not.label)
      notUsedInExpression = true;
  }
  if (endIndex === expressionArr.length - 1) endOfString = true;

  return { endIndex, endOfString, notUsedInExpression };
};

export const buildACStructureNode = (children, label = '', notNode = false) => {
  const { toLabelString, toDefString } = AutocodeLabelsGenerator(
    ACStandardLabels,
    null
  );
  const { buildObjectsAddedArray } = AutocodeExpressionListGenerator();

  let data = {};
  let type = AC_NODETYPE_DATAPOINT;

  if (notNode) {
    data = {
      funcName: 'NOT',
      selected: false,
      editable: true,
      params: null,
    };
    type = AC_NODETYPE_FUNC;
  }

  return {
    type,
    id: uuid(),
    selected: false,
    data,
    children: cloneDeep(children),
    group: 0,
    rootnodeChild: AC_NODETYPE_ROOT,
    useLabelOverride: label.length > 0,
    labelOverride: label.length > 0 ? label : '',
    combine() {
      return combineAllChildren(this);
    },
    toLabelString(pts) {
      return toLabelString(this, pts);
    },
    toExpressionString() {
      return toDefString(this);
    },
    toExpressionArray(pts) {
      return buildObjectsAddedArray(this, pts);
    },
  };
};

export const buildAutocodenodeWithNot = (expList, keywordList, label) => {
  const expressionList = removeStartEndParanthesis(expList);
  let acnode = {};
  const keywordsArray = expressionList.filter(item => checkKeywords(item));
  const objectsAdded = [];
  const children = [];
  if (expressionList[0] === AC_OPERATORS.type.not.label) {
    if (keywordsArray.length === 1) {
      // for single not expressions Eg: 'NOT MALE'
      objectsAdded.push(
        buildDictionaryItem(keywordsArray[0], keywordList[keywordsArray[0]])
      );
      const childNode = buildAutoCodeObject(
        keywordsArray,
        objectsAdded,
        keywordList
      );
      if (!isEmpty(childNode)) {
        childNode.rootnodeChild = AC_NODETYPE_FUNC;
        children.push(childNode);
      }
      acnode = buildACStructureNode(children, '', true);
    } else if (!expressionList.includes('(')) {
      // for expressions like  'NOT MALE AND FEMALE OR AGE18 OR AGE19'
      objectsAdded.push(
        buildDictionaryItem(expressionList[1], keywordList[expressionList[1]])
      );

      let firstChildNode = buildAutoCodeObject(
        expressionList[1],
        objectsAdded,
        keywordList[expressionList[1]]
      );
      let remainingNode = {};
      const combiningOperator = addOperatorItem(expressionList[2]);

      firstChildNode.rootnodeChild = AC_NODETYPE_FUNC;
      children.push(cloneDeep(firstChildNode));
      firstChildNode = buildACStructureNode(children, '', true);
      children.pop();
      children.push(cloneDeep(firstChildNode));
      // console.log(firstChildNode);
      objectsAdded.pop();
      objectsAdded.push(
        ...getObjectAdded(expressionList.slice(3), keywordList)
      );
      remainingNode = buildAutoCodeObject(
        expressionList.slice(3),
        objectsAdded,
        keywordList
      );
      // console.log(remainingNode);
      children.push(cloneDeep(remainingNode));

      acnode = createAutoCodeObject(
        cloneDeep(children),
        combiningOperator,
        AC_NODETYPE_OPERATOR,
        'root',
        false,
        ''
      );
    } else {
      // for expressions like ' NOT ( RACEWHT  OR AGE21 )  XOR RACEORI'  or  'NOT ( RACEWHT  OR RACEORI )'
      const {
        endIndex,
        endOfString,
        notUsedInExpression,
      } = checkAndRemoveBalancedParanthesis(expressionList);

      if (endOfString) {
        // For 'NOT ( RACEWHT  OR RACEORI )'
        if (notUsedInExpression) {
          // Need to recheck this scenario
          // children.push(
          //   buildAutocodenodeWithNot(expressionList.slice(1, endIndex))
          // );
        } else {
          // for Operators or Single dictionary datapoints '( RACEWHT  OR AGE21 AND MALE )' or '( RACEWHT )'
          objectsAdded.push(
            ...getObjectAdded(expressionList.slice(1), keywordList)
          );
          const childNode = buildAutoCodeObject(
            expressionList.slice(1),
            objectsAdded,
            keywordList
          );
          if (!isEmpty(childNode)) {
            childNode.rootnodeChild = AC_NODETYPE_FUNC;
            children.push(cloneDeep(childNode));
          }
          acnode = buildACStructureNode(children, '', true);
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (notUsedInExpression) {
          // Need to recheck this scenario
          // children.push(
          //   buildAutocodenodeWithNot(expressionList.slice(1, endIndex))
          // );
        } else {
          // for Operators or Single dictionary datapoints 'NOT ( RACEWHT  OR AGE21 )  XOR RACEORI'
          // or 'NOT ( RACEWHT)  OR ( RACEORI AND MALE )'
          objectsAdded.push(
            ...getObjectAdded(
              expressionList.slice(1, endIndex + 1),
              keywordList
            )
          );
          let firstChildNode = buildAutoCodeObject(
            expressionList.slice(1, endIndex + 1),
            objectsAdded,
            keywordList
          );
          firstChildNode.rootnodeChild = AC_NODETYPE_FUNC;

          children.push(cloneDeep(firstChildNode));
          firstChildNode = buildACStructureNode(children, '', true);
          children.pop();
          children.push(cloneDeep(firstChildNode));
          objectsAdded.splice(0, objectsAdded.length);
          objectsAdded.push(
            ...getObjectAdded(expressionList.slice(endIndex + 2), keywordList)
          );
          const combiningOperator = addOperatorItem(
            expressionList[endIndex + 1]
          );
          const remainingNode = buildAutoCodeObject(
            expressionList.slice(endIndex + 2),
            objectsAdded,
            keywordList
          );
          children.push(cloneDeep(remainingNode));

          acnode = createAutoCodeObject(
            cloneDeep(children),
            combiningOperator,
            AC_NODETYPE_OPERATOR,
            'root',
            false,
            ''
          );
        }
      }
    }
  }
  if (isEmpty(acnode)) {
    acnode = buildACStructureNode([], label, true);
  }

  return acnode;
};

// refer to ReferFile.txt for abbreviations of import SPC 2.0
export const removeAttributesFromACNode = acnode => {
  const removeProps = node => {
    const updatedNode = {
      _ch: node.children,
      _d: node.data,
      _lo: node.labelOverride,
      _rnc: node.rootnodeChild,
      _ty: node.type,
      _ulo: node.useLabelOverride,
    };

    if (node.type === AC_NODETYPE_DATAPOINT) {
      if (!isEmpty(node.data)) {
        updatedNode.p = node.p;
        let data = {};
        if (node.data.keyword && node.data.keyword.split(' ').length > 1) {
          // for items having keyword as RHERCUBA > 0 or RHERCUBA =0
          data = {
            _l: node.data.labels,
            _k: node.data.keyword,
            _p: node.data.path,
            _vk: node.data.volKeyword,
            _temp: node.data.template,
            _st: node.data.shortText,
            _b: node.data.breadcrumbs,
            _dt: node.data.datatype,
            _n: node.data.node,
            _nl: node.data.nodeLabel,
            _tp: node.data.textPath,
            _tip: node.data.timePeriod,
          };
        } else {
          data = {
            _l: node.data.labels,
            _k: node.data.keyword,
            _p: node.data.path,
            _vk: node.data.volKeyword,
            _temp: node.data.template,
            _tip: node.data.timePeriod,
          };
        }
        updatedNode._d = data;
      }
    } else if (node.type === AC_NODETYPE_DEFINITION) {
      const data = {
        _b: node.data.breadcrumbs,
        _exp: node.data.expression,
        _expid: node.data.expressionId,
        _ovdid: node.data.ovDefinitionId,
        _sc: node.data.scope,
        _ti: node.data.title,
        _dis: node.data.display,
      };
      if (node.data.selectedFolder && node.data.selectedFolder.definitionId) {
        data._sf = {
          _did: node.data.selectedFolder.definitionId,
        };
      }
      updatedNode._d = data;
    } else if (node.type === AC_NODETYPE_OPERATOR) {
      // handles Operator nodes
      const data = {
        _ty: {
          _l: node.data.type.label,
          _sy: node.data.type.symbol,
        },
      };

      updatedNode._d = data;
      updatedNode.p = node.p;
      const updatedchildren = [];
      updatedchildren.push(removeProps(node.children[0]));
      updatedchildren.push(removeProps(node.children[1]));
      updatedNode._ch = updatedchildren;
    } else if (node.type === AC_NODETYPE_FUNC) {
      // handles Function nodes
      const data = {
        _fn: node.data.funcName,
      };
      data._para = null;
      if (node.data.funcName === 'net any') {
        if (node.data.operator) {
          data._op = {
            _l: node.data.operator.label,
            _sy: node.data.operator.symbol,
          };
        } else {
          data._para = {};
          if (node.data.params) {
            data._para._op = {
              _l: node.data.params.operator.label,
              _sy: node.data.params.operator.symbol,
            };
          }
        }
      } else if (node.data.params) {
        if (
          node.data.params.lower !== undefined &&
          node.data.params.upper !== undefined
        ) {
          data._para = {
            _low: node.data.params.lower,
            _up: node.data.params.upper,
          };
        }
      }
      updatedNode._d = data;
      const updatedchildren = [];
      node.children.forEach(item => {
        updatedchildren.push(removeProps(item));
      });
      updatedNode._ch = updatedchildren;
    }
    return updatedNode;
  };

  const updNode = removeProps(acnode);
  // console.log('Autocode Node:', acnode);
  // console.log('Updated Node:', udNode);
  return updNode; // removeProps(acnode);
};

export const getHighlightandFilteringRules = highlightAndFilterRules => {
  const getModifiedRules = type => {
    let _hl = [];
    let _fil = [];

    if (type.highlight.length) {
      _hl = type.highlight.map(item => ({
        _val: item.value,
        _op: item.operator,
        _txtcol: item.textColor,
        _celcol: item.cellColor,
      }));
    }

    if (type.filter.length) {
      _fil = type.filter.map(item => ({
        _val: item.value,
        _op: item.operator,
      }));
    }

    return { _hl, _fil };
  };

  const rules = {
    sample: {},
    weighted: {},
    vertical: {},
    horizontal: {},
    index: {},
  };

  rules.sample = getModifiedRules(highlightAndFilterRules.sample);
  rules.weighted = getModifiedRules(highlightAndFilterRules.weighted);
  rules.vertical = getModifiedRules(highlightAndFilterRules.vertical);
  rules.horizontal = getModifiedRules(highlightAndFilterRules.horizontal);
  rules.index = getModifiedRules(highlightAndFilterRules.index);

  return rules;
};

export const buildSPC2Object = (
  weightType,
  title,
  divisor,
  products,
  basesData,
  rowsData,
  columnsData,
  partsToShow,
  conditionalFormatting,
  highlightAndFilterRules
) => {
  const { toLabelString } = AutocodeLabelsGenerator(ACStandardLabels, null);

  const bases = basesData.length
    ? basesData.map(node => {
        return {
          keyword: node.keyword,
          title: truncateDefLabel(toLabelString(node.dataSmartSearch.autocodenode, partsToShow),
            MAX_EXPORT_LABEL_LIMIT),
          acnode: removeAttributesFromACNode(node.dataSmartSearch.autocodenode),
        };
      })
    : [];
  const columns = columnsData.length
    ? columnsData.map(node => {
        return {
          keyword: node.keyword,
          title: truncateDefLabel(toLabelString(node.dataSmartSearch.autocodenode, partsToShow),
            MAX_EXPORT_LABEL_LIMIT),
          acnode: removeAttributesFromACNode(node.dataSmartSearch.autocodenode),
        };
      })
    : [];
  const rows = rowsData.length
    ? rowsData.map(node => {
        return {
          keyword: node.keyword,
          title: truncateDefLabel(toLabelString(node.dataSmartSearch.autocodenode, partsToShow),
            MAX_EXPORT_LABEL_LIMIT),
          acnode: removeAttributesFromACNode(node.dataSmartSearch.autocodenode),
        };
      })
    : [];

  const spcData = {
    version: 3,
    weightType,
    title,
    divisor,
    products: [...products],
    attributes: {
      hnf: {
        formatting: conditionalFormatting !== 'DefaultFilter',
        rules: getHighlightandFilteringRules(highlightAndFilterRules),
      },
      bases,
      columns,
      rows,
    },
  };

  return spcData;
};

export const checkSPCForVersion = (obj, savedCrosstab = false) => {
  let spcObject;
  if (!savedCrosstab) {
    try {
      spcObject = JSON.parse(obj);
    } catch (e) {
      return false;
    }
  } else {
    spcObject = cloneDeep(obj);
  }
  if (
    spcObject &&
    spcObject.version &&
    spcObject.version === 3 &&
    typeof spcObject === 'object'
  ) {
    return true;
  }
  return false;
};

export const setHighlightandFilteringRules = highlightAndFilterRules => {
  const getModifiedRules = type => {
    let highlight = [];
    let filter = [];

    if (type._hl.length) {
      highlight = type._hl.map(item => ({
        value: item._val,
        operator: item._op,
        textColor: item._txtcol,
        cellColor: item._celcol,
      }));
    }

    if (type._fil.length) {
      filter = type._fil.map(item => ({
        value: item._val,
        operator: item._op,
      }));
    }

    return { highlight, filter };
  };

  const rules = {
    sample: {},
    weighted: {},
    vertical: {},
    horizontal: {},
    index: {},
  };

  rules.sample = getModifiedRules(highlightAndFilterRules.sample);
  rules.weighted = getModifiedRules(highlightAndFilterRules.weighted);
  rules.vertical = getModifiedRules(highlightAndFilterRules.vertical);
  rules.horizontal = getModifiedRules(highlightAndFilterRules.horizontal);
  rules.index = getModifiedRules(highlightAndFilterRules.index);

  return rules;
};

export const rebuildGridObjectsFromNewSPC = ({
  uid,
  accountId,
  spcObject,
  partsToShow,
  productNo,
}) => {
  const keywordList = acnode => {
    const keyList = [];
    const makeKeywordList = node => {
      if (node._ty === AC_NODETYPE_DATAPOINT) {
        if (
          !isEmpty(node._d) &&
          node._d._k &&
          !GEOGRAPHY_BASES.some(item => node._d._k.includes(item))
        ) {
          if (
            getExpressionFormed(node._d._k).filter(item => checkKeywords(item))
              .length === 1
          )
            keyList.push(node._d._k);
        }
      } else if (node._ty === AC_NODETYPE_OPERATOR) {
        makeKeywordList(node._ch[0]);
        makeKeywordList(node._ch[1]);
      } else if (node._ty === AC_NODETYPE_FUNC) {
        node._ch.forEach(item => makeKeywordList(item));
      }
    };
    makeKeywordList(acnode);
    return keyList;
  };

  const bases = spcObject.attributes.bases;
  const rows = spcObject.attributes.rows;
  const columns = spcObject.attributes.columns;

  const highlightAndFilter = setHighlightandFilteringRules(
    spcObject.attributes.hnf.rules
  );

  return {
    uid,
    accountId,
    spcObject,
    products: productNo,
    title: spcObject.title,
    version: spcObject.version,
    divisor: spcObject.divisor,
    weightType: spcObject.weightType,
    basesKeywordList: bases.reduce((acc, item) => {
      acc.push(...keywordList(item.acnode));
      return acc;
    }, []),
    rowsKeywordList: rows.reduce((acc, item) => {
      acc.push(...keywordList(item.acnode));
      return acc;
    }, []),
    columnKeywordList: columns.reduce((acc, item) => {
      acc.push(...keywordList(item.acnode));
      return acc;
    }, []),
    bases,
    rows,
    columns,
    partsToShow,
    chartType: spcObject.attributes.chartType
      ? spcObject.attributes.chartType
      : 'Private Eye',
    conditionalFormatting: spcObject.attributes.hnf.formatting
      ? 'HNF'
      : 'DefaultFilter',
    highlightAndFilter,
  };
};

export const addAttributesToACNode = (acnode, DictionaryDetails) => {
  const { toLabelString, toDefString } = AutocodeLabelsGenerator(
    ACStandardLabels,
    null
  );
  const { buildObjectsAddedArray } = AutocodeExpressionListGenerator();
  const d = new Date();

  const addProps = node => {
    const updatedNode = {
      children: node._ch,
      data: node._d,
      labelOverride: node._lo,
      rootnodeChild: node._rnc,
      type: node._ty,
      useLabelOverride: node._lo ? node._lo.trim() !== '' : false,
      id: uuid(),
      selected: false,
      group: 0,
      combine() {
        return combineAllChildren(this);
      },
      toLabelString(pts) {
        return toLabelString(this, pts);
      },
      toExpressionString() {
        return toDefString(this);
      },
      toExpressionArray(pts) {
        return buildObjectsAddedArray(this, pts);
      },
    };

    if (node._ty === AC_NODETYPE_DATAPOINT) {
      if (!isEmpty(node._d)) {
        updatedNode.p = node.p;
        let data = {};
        if (
          node._d._k &&
          (getExpressionFormed(node._d._k).filter(item => checkKeywords(item))
            .length > 1 ||
            GEOGRAPHY_BASES.some(item => node._d._k.includes(item)))
        ) {
          // for items having keyword as RHERCUBA > 0 or RHERCUBA =0
          data = {
            labels: node._d._l,
            keyword: node._d._k,
            path: node._d._p,
            volKeyword: node._d._vk,
            template: node._d._temp,
            shortText: node._d._st,
            breadcrumbs: node._d._b,
            datatype: node._d._dt,
            node: node._d._n,
            nodeLabel: node._d._nl,
            textPath: node._d._tp,
            timePeriod: node._d._tip,
            midpoint: null,
            modeled: false,
            rectype: node._d.rectype,
            shortTextCatgView: node._d._l.length
              ? node._d._l[node._d._l.length - 1]
              : '',
            uniqueId: uuid(),
          };
        } else {
          const dictionaryData = DictionaryDetails[dictionaryDetailExp([node._d._k])];
          const nodeLabel = dictionaryData.templateType
            ? dictionaryData.templateType === '1'
              ? dictionaryData.labels.filter(
                  item =>
                    item.toUpperCase() === 'BRAND' ||
                    item.toUpperCase() === 'CATEGORY' ||
                    item.toUpperCase() === 'TYPE'
                )[0]
              : dictionaryData.labels[dictionaryData.labels.length - 2]
            : '';
          data = {
            labels: node._d._l,
            template: node._d._temp,
            nodeLabel,
            datatype: dictionaryData.recType,
            keyword: node._d._k,
            path: node._d._p,
            node: dictionaryData.node,
            midpoint: dictionaryData.midpoint,
            uniqueId: uuid(),
            modeled: false,
            textPath: dictionaryData.textPath,
            shortText: `${
              dictionaryData.labels[dictionaryData.labels.length - 2]
            }: ${dictionaryData.labels[dictionaryData.labels.length - 1]}`,
            rectype: dictionaryData.recTag,
            shortTextCatgView: `${
              dictionaryData.labels[dictionaryData.labels.length - 1]
            }`,
            breadcrumbs: dictionaryData.labels.join(' > '),
            volKeyword: node._d._vk,
            timePeriod: node._d._tip,
          };
        }
        updatedNode.data = data;
      }
    } else if (node._ty === AC_NODETYPE_DEFINITION) {
      const data = {
        breadcrumbs: node._d._b,
        expression: node._d._exp,
        expressionId: node._d._expid,
        lastModified: `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`,
        ovDefinitionId: node._d._ovdid,
        scope: node._d._sc,
        title: node._d._ti,
        display: node._d._dis,
      };

      if (node._d && !isEmpty(node._d._sf)) {
        data.selectedFolder = {
          definitionId: node._d._sf._did,
          folderName: node._d._b.split(' > ')[1],
          lastModified: `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`,
          scope: node._d._sc,
        };
      }
      updatedNode.data = data;
    } else if (node._ty === AC_NODETYPE_OPERATOR) {
      // handles Operator nodes
      const data = {
        selected: false,
        type: {
          label: node._d._ty._l,
          symbol: node._d._ty._sy,
        },
      };

      updatedNode.data = data;
      updatedNode.p = node.p;
      const updatedchildren = [];
      updatedchildren.push(addProps(node._ch[0]));
      updatedchildren.push(addProps(node._ch[1]));
      updatedNode.children = updatedchildren;
    } else if (node._ty === AC_NODETYPE_FUNC) {
      // handles Function nodes
      const data = {
        selected: false,
        editable: true,
      };

      if (node._d._fn) {
        data.funcName = node._d._fn;
      } else {
        data.funcName = null;
        data.editable = false;
      }

      data.params = null;
      if (
        node._d._para &&
        node._d._para._low !== undefined &&
        node._d._para._up !== undefined
      ) {
        data.params = {
          operator: {},
          lower: node._d._para._low,
          upper: node._d._para._up,
        };
      } else if (node._d._fn === 'net any') {
        if (node._d._para && node._d._para._op) {
          data.params = {};
          data.params.operator = {
            label: node._d._para._op._l,
            symbol: node._d._para._op._sy,
          };
        } else if (node._d._op) {
          data.operator = {
            label: node._d._op._l,
            symbol: node._d._op._sy,
          };
        }
      }
      updatedNode.data = data;
      const updatedchildren = [];
      node._ch.forEach(item => updatedchildren.push(addProps(item)));
      updatedNode.children = updatedchildren;
    }
    return updatedNode;
  };

  const updNode = addProps(acnode);
  // console.log('ACNode:', acnode);
  // console.log('AutocodeNode:', updNode);
  return updNode; // removeProps(acnode);
};

export const rebuildAutocodeNodesForSPC = (
  SpcStructureParams,
  DictionaryDetails
) => {
  const newSpcStructureParams = cloneDeep(SpcStructureParams);
  const { toLabelString } = AutocodeLabelsGenerator(ACStandardLabels, null);

  const labelDetails = {
    bases: [],
    rows: [],
    columns: [],
  };
  const setLabelDetails = (item, index, grid) => {
    if (
      isEmpty(item.autocodenode.data) &&
      item.autocodenode.type === 'dictionary' &&
      item.autocodenode.useLabelOverride
    )
      labelDetails[grid].push({
        isLabelUpdated: false,
        index,
        keyword: item.keyword,
        originalTitle: item.title,
        updatedTitle: item.title,
      });
    else if (
      isEmpty(item.autocodenode.data) ||
      (item.autocodenode.data.funcName &&
        item.autocodenode.data.funcName !== 'NOT' &&
        item.autocodenode.data.funcName !== 'SUM' &&
        item.autocodenode.data.funcName !== 'net any' &&
        isEmpty(item.autocodenode.data.params))
    ) {
      labelDetails[grid].push({
        isLabelUpdated: item.autocodenode.useLabelOverride,
        index,
        keyword: item.keyword,
        originalTitle: item.title,
        updatedTitle: item.title,
      });
    } else {
      labelDetails[grid].push({
        isLabelUpdated: item.autocodenode.useLabelOverride,
        index,
        keyword: item.keyword,
        originalTitle: item.autocodenode.useLabelOverride
          ? toLabelString(
              item.autocodenode,
              newSpcStructureParams.partsToShow,
              true
            )
          : item.title,
        updatedTitle: item.title,
      });
    }
  };
  // eslint-disable-next-line no-unused-expressions
  newSpcStructureParams.bases =
    newSpcStructureParams.bases && newSpcStructureParams.bases.length > 0
      ? newSpcStructureParams.bases.map(item => ({
          keyword: item.keyword,
          title: item.title,
          autocodenode: addAttributesToACNode(
            item.acnode,
            DictionaryDetails.BasesGrid
          ),
        }))
      : [];

  newSpcStructureParams.rows =
    newSpcStructureParams.rows && newSpcStructureParams.rows.length > 0
      ? newSpcStructureParams.rows.map(item => ({
          keyword: item.keyword,
          title: item.title,
          autocodenode: addAttributesToACNode(
            item.acnode,
            DictionaryDetails.RowsGrid
          ),
        }))
      : [];

  newSpcStructureParams.columns =
    newSpcStructureParams.columns && newSpcStructureParams.columns.length > 0
      ? newSpcStructureParams.columns.map(item => ({
          keyword: item.keyword,
          title: item.title,
          autocodenode: addAttributesToACNode(
            item.acnode,
            DictionaryDetails.ColumnsGrid
          ),
        }))
      : [];

  newSpcStructureParams.bases.forEach((item, index) =>
    setLabelDetails(item, index, 'bases')
  );
  newSpcStructureParams.rows.forEach((item, index) =>
    setLabelDetails(item, index, 'rows')
  );
  newSpcStructureParams.columns.forEach((item, index) =>
    setLabelDetails(item, index, 'columns')
  );

  return { ...newSpcStructureParams, labelDetails };
};

export const getSampleWeightedParamsForAutocodeNodes = (
  contents,
  partsToShow
) => {
  const { buildObjectsAddedArray } = AutocodeExpressionListGenerator();
  const expressionList = [];
  const selectedNames = [];
  const dataSmartSearch = [];
  const isLabelUpdated = [];
  // console.log('Contents: ', contents);

  if (contents.length > 0) {
    // eslint-disable-next-line no-restricted-syntax
    for (const node of contents) {
      if (node.type === AC_NODETYPE_DEFINITION) {
        const tmpDataSmartSearch = {
          expressionFormed: [],
          objectsAdded: [],
          tags: [],
          autocodenode: null,
        };
        node.data.shortTextCatgView = node.data.title;
        expressionList.push(node.data.expression);
        selectedNames.push(node.data.title);
        tmpDataSmartSearch.expressionFormed.push(node.data.expression);
        tmpDataSmartSearch.objectsAdded.push(node.data);
        tmpDataSmartSearch.autocodenode = node;
        tmpDataSmartSearch.tags.push(node.data.title);
        dataSmartSearch.push(tmpDataSmartSearch);
        isLabelUpdated.push(node.useLabelOverride);
      } else if (node.type === AC_NODETYPE_OPERATOR) {
        const tmpDataSmartSearch = {
          expressionFormed: [],
          objectsAdded: [],
          tags: [],
        };
        // let expressionListString = '';
        // let selectedNamesString = '';
        tmpDataSmartSearch.objectsAdded.push(
          ...buildObjectsAddedArray(node, partsToShow)
        );
        tmpDataSmartSearch.autocodenode = node;

        // tmpDataSmartSearch.objectsAdded.forEach(item => {
        //   if (item.expression && item.title) {
        //     tmpDataSmartSearch.expressionFormed.push(item.expression);
        //     tmpDataSmartSearch.tags.push(item.title);
        //     expressionListString = `${expressionListString} ${item.expression}`;
        //     selectedNamesString = `${selectedNamesString} ${item.title}`;
        //   } else {
        //     expressionListString = `${expressionListString} ${item.keyword}`;
        //     selectedNamesString = `${selectedNamesString} ${item.keyword}`;
        //   }
        // });
        tmpDataSmartSearch.expressionFormed.push(node.toExpressionString());
        expressionList.push(node.toExpressionString());
        selectedNames.push(node.toLabelString(partsToShow));
        // expressionList.push(expressionListString);
        // selectedNames.push(selectedNamesString);
        isLabelUpdated.push(node.useLabelOverride);
        dataSmartSearch.push(tmpDataSmartSearch);
      } else if (node.type === AC_NODETYPE_DATAPOINT) {
        const tmpDataSmartSearch = {
          expressionFormed: [],
          objectsAdded: [],
          tags: [],
        };
        const updatedACnode = cloneDeep(node);
        if (
          Object.keys(node.data).length === 1 &&
          Object.keys(node.data)[0] === 'keyword'
        ) {
          updatedACnode.data = {};
        }
        // node.data.shortTextCatgView = node.data.title;
        expressionList.push(node.toExpressionString());
        selectedNames.push(node.toLabelString(partsToShow));
        tmpDataSmartSearch.expressionFormed.push(node.toExpressionString());
        tmpDataSmartSearch.objectsAdded.push(node.data);
        tmpDataSmartSearch.autocodenode = updatedACnode;
        tmpDataSmartSearch.tags.push(node.toLabelString(partsToShow));
        dataSmartSearch.push(tmpDataSmartSearch);
        isLabelUpdated.push(node.useLabelOverride);
      } else if (node.type === AC_NODETYPE_FUNC) {
        // jedK needs to be reviewed
        const tmpDataSmartSearch = {
          expressionFormed: [],
          objectsAdded: [],
          tags: [],
        };
        // node.data.shortTextCatgView = node.data.title;
        expressionList.push(node.toExpressionString());
        selectedNames.push(node.toLabelString(partsToShow));
        tmpDataSmartSearch.expressionFormed.push(node.toExpressionString());
        tmpDataSmartSearch.objectsAdded = getAllDefintionsorDictionaryNodes(
          node
        );
        tmpDataSmartSearch.autocodenode = node;
        tmpDataSmartSearch.tags.push(node.toLabelString(partsToShow));
        dataSmartSearch.push(tmpDataSmartSearch);
        isLabelUpdated.push(node.useLabelOverride); // node.isLabelUpdated is being hardcoded to true
        // now to be in align with dictionary search functions
      }
    }
  }
  return {
    selectedKeywords: expressionList,
    selectedNames,
    originalNames: selectedNames,
    dataSmartSearch,
    isLabelUpdated,
  };
};

// Takes definition objects and fetches their saved node objects
// or attempts to parse their expressions to create a node object
export const getNodeObjectsForDefinitionObjects = async (definitionObjects, userId, accountId, productNo) => {
  // Defs with saved node objects have editable flag true
  const editableDefs = definitionObjects.filter(c => c.editable);

  // Defs without saved node objects
  const uneditableDefs = definitionObjects.filter(c => !c.editable);

  // Get all keywords in all expressions in SB
  const keywordsList = getKeywordList(definitionObjects, false);

  // Fetch dictionary objects for all keywords in all expressions in SB
  // dictionaryResponse is object mapping keywods to dictionary objects
  const dictionaryResponse = await getDicForDefKeywords(
    userId,
    accountId,
    productNo,
    keywordsList
  );

  // Array to return resulting nodes.
  const nodes = [];

  if (editableDefs.length > 0) {
    // Expressions of defs with saved node objects
    const editableDefsExpressions = editableDefs.map(d => d.expressionId);

    // Fetch all saved node objects
    // nodeObjectReponse is object mapping expression ids to saved node objects
    const nodeObjectReponse = await fetchNodeObjectForDefinition(
      accountId,
      editableDefsExpressions
    );

    // Saved node objects are in SPC format, transform them to full format
    const nodesWithSavedNodeObject = Object.values(
      nodeObjectReponse
    ).map(acnode => addAttributesToACNode(acnode, dictionaryResponse));

    nodes.push(...nodesWithSavedNodeObject);
  }

  if (uneditableDefs.length > 0) {
    // For defs without saved node object attempt to parse expressions to create node objects
    const nodesWithoutSavedNodeObject = createDictionaryElementForKeyword(
      dictionaryResponse,
      uneditableDefs,
      false
    );

    // Current unsupported operators in selection box
    // Attempting to parse expressions with these may be successful
    // but inserting the resulting nodes to selection box will result in error
    const unsupported = ["*", "/", "<", ">", "+", "-"];

    // Non parsable expressions result in empty node objects
    // Replace empty objects with the definition object
    const nonEmptyNodes = nodesWithoutSavedNodeObject.map((item, i) => {
      return isEmpty(item) || isEmpty(item.children) || unsupported.some(c => uneditableDefs[i].expression.includes(c))
        ? wrapNode(AC_NODETYPE_DEFINITION, uneditableDefs[i])
        : item;
    });

    nodes.push(...nonEmptyNodes);
  }

  // Note: this will rearrange order: editable first followed by non-editable
  return nodes;
};
